import { useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Button } from "../../../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { ArrowLeft, Loader2, Power, PowerOff, ShieldCheck } from "lucide-react";
import { cn } from "../../../lib/utils";

/** Served from `public/engine-access-vehicle.png` */
const ENGINE_ACCESS_VEHICLE_IMG = "/engine-access-vehicle.png";

export default function EngineAccessPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { deviceId: deviceIdParam } = useParams();

  const deviceId = deviceIdParam ? decodeURIComponent(deviceIdParam) : "";
  const mapBasePath = location.pathname.startsWith("/admin") ? "/admin" : "/customer";

  const [ignitionOn, setIgnitionOn] = useState(false);
  const [busy, setBusy] = useState(false);

  const goBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate(`${mapBasePath}/map/dashboard`);
  };

  const sendIgnitionCommand = async (nextOn) => {
    setBusy(true);
    try {
      /* Wire e.g. POST /devices/${deviceId}/ignition { on: nextOn } when API is ready */
      await new Promise((r) => setTimeout(r, 450));
      setIgnitionOn(nextOn);
      toast.success(nextOn ? "Ignition turned on" : "Ignition turned off");
    } catch {
      toast.error("Could not update ignition. Try again.");
    } finally {
      setBusy(false);
    }
  };

  if (!deviceId) {
    return (
      <div className="engine-access-root flex min-h-screen flex-col items-center justify-center bg-background p-6 text-foreground">
        <p className="mb-2 text-center text-sm font-medium text-destructive">Missing device</p>
        <p className="mb-6 max-w-sm text-center text-xs text-muted-foreground">Open engine access from a vehicle on the map.</p>
        <Button variant="outline" onClick={() => navigate(`${mapBasePath}/map/dashboard`)}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Map dashboard
        </Button>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground">
      <header className="sticky top-0 z-20 shrink-0 border-b bg-background/90 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center gap-2 px-4 py-3 sm:px-6">
          <Button type="button" variant="ghost" size="icon" className="shrink-0 rounded-full" onClick={goBack} title="Back">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-lg font-bold tracking-tight sm:text-xl">Engine Access</h1>
            <p className="truncate text-xs text-muted-foreground tabular-nums" title={deviceId}>
              Device {deviceId}
            </p>
          </div>
        </div>
      </header>

      <main className="mx-auto flex w-full max-w-2xl flex-1 flex-col gap-5 px-4 pb-12 pt-4 sm:px-6 sm:pt-6">
        <div className="flex flex-col items-center">
          <div className="relative w-full max-h-[min(42vh,360px)] sm:max-h-[min(48vh,420px)]">
            <span className="sr-only" aria-live="polite">
              {ignitionOn ? "Headlights on (visual effect)" : "Headlights off"}
            </span>
            <img
              src={ENGINE_ACCESS_VEHICLE_IMG}
              alt="Vehicle"
              draggable={false}
              className={cn(
                "relative z-0 w-full max-h-[min(42vh,360px)] object-contain object-top transition-[filter] duration-500 sm:max-h-[min(48vh,420px)]",
                ignitionOn && "brightness-[1.07] contrast-[1.02]"
              )}
            />
            {/* Simulated headlight glow (single PNG — adjust % if art changes) */}
            <div
              className={cn(
                "pointer-events-none absolute inset-0 z-10 transition-opacity duration-700 ease-out",
                ignitionOn ? "opacity-100" : "opacity-0"
              )}
              aria-hidden
            >
              <div
                className="absolute h-[18%] min-h-[48px] w-[22%] min-w-[72px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-amber-100/90 blur-2xl mix-blend-screen dark:bg-amber-200/70"
                style={{ left: "74%", top: "41%" }}
              />
              <div
                className="absolute h-[14%] min-h-[40px] w-[16%] min-w-[56px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/80 blur-xl mix-blend-screen dark:bg-amber-50/50"
                style={{ left: "66%", top: "44%" }}
              />
              <div className="absolute bottom-[6%] left-[32%] right-[4%] h-[16%] rounded-[100%] bg-amber-200/20 blur-3xl mix-blend-screen dark:bg-amber-300/15" />
            </div>
          </div>
          <p className="mt-2 text-center text-[11px] text-muted-foreground">
            {ignitionOn ? "Headlights on (preview effect on static image)" : "Turn ignition on to preview headlights"}
          </p>
        </div>

        <Card className="border-border/60 shadow-sm">
          <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0 pb-4">
            <div className="space-y-1">
              <CardTitle className="text-base font-semibold">Ignition status</CardTitle>
              <CardDescription className="text-xs">Remote command applies to this device only.</CardDescription>
            </div>
            <Badge
              variant="secondary"
              className={cn(
                "shrink-0 border px-3 py-1 text-xs font-semibold",
                ignitionOn
                  ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
                  : "border-border bg-muted/80 text-muted-foreground"
              )}
            >
              {ignitionOn ? "Ignition on" : "Ignition off"}
            </Badge>
          </CardHeader>
        </Card>

        <div className="grid gap-4 sm:grid-cols-2">
          <Card
            className={cn(
              "border-border/60 shadow-sm transition-all duration-200",
              ignitionOn && "ring-2 ring-emerald-500/45 border-emerald-500/35 shadow-md"
            )}
          >
            <CardHeader className="pb-3">
              <div className="flex items-start gap-3">
                <div
                  className={cn(
                    "flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl",
                    ignitionOn ? "bg-emerald-500 text-white" : "bg-emerald-500/12 text-emerald-700 dark:text-emerald-400"
                  )}
                  aria-hidden
                >
                  <Power className="h-5 w-5" strokeWidth={2.25} />
                </div>
                <div className="min-w-0 space-y-1">
                  <CardTitle className="text-lg font-semibold leading-tight">Ignition on</CardTitle>
                  <CardDescription className="text-xs leading-relaxed">
                    Enables remote start sequence for this vehicle.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <Button
                type="button"
                className="h-11 w-full rounded-xl font-semibold shadow-sm"
                disabled={busy || ignitionOn}
                onClick={() => sendIgnitionCommand(true)}
              >
                {busy && !ignitionOn ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending…
                  </>
                ) : ignitionOn ? (
                  "Already on"
                ) : (
                  "Turn ignition on"
                )}
              </Button>
            </CardContent>
          </Card>

          <Card
            className={cn(
              "border-border/60 shadow-sm transition-all duration-200",
              !ignitionOn && "ring-2 ring-slate-400/35 border-slate-300/40 dark:border-slate-600/50 dark:ring-slate-500/30"
            )}
          >
            <CardHeader className="pb-3">
              <div className="flex items-start gap-3">
                <div
                  className={cn(
                    "flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl",
                    !ignitionOn ? "bg-slate-700 text-white dark:bg-slate-600" : "bg-muted text-muted-foreground"
                  )}
                  aria-hidden
                >
                  <PowerOff className="h-5 w-5" strokeWidth={2.25} />
                </div>
                <div className="min-w-0 space-y-1">
                  <CardTitle className="text-lg font-semibold leading-tight">Ignition off</CardTitle>
                  <CardDescription className="text-xs leading-relaxed">
                    Stops engine and locks remote start until turned on again.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <Button
                type="button"
                variant="outline"
                className="h-11 w-full rounded-xl border-2 font-semibold"
                disabled={busy || !ignitionOn}
                onClick={() => sendIgnitionCommand(false)}
              >
                {busy && ignitionOn ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending…
                  </>
                ) : !ignitionOn ? (
                  "Already off"
                ) : (
                  "Turn ignition off"
                )}
              </Button>
            </CardContent>
          </Card>
        </div>

        <Card className="border-dashed border-border/70 bg-muted/25 shadow-none">
          <CardContent className="flex gap-3 py-4">
            <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" aria-hidden />
            <p className="text-xs leading-relaxed text-muted-foreground">
              Use ignition controls only when it is safe and legal. Commands are simulated here until your backend
              endpoint is connected.
            </p>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
